import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { addGalleryDoneTick, setCollectedFirstOwner, } from "../api/gallery/galleryCollectedPlayers";
import { getGalleryProgress, setGalleryCompletedGrade, startGallerySolves, } from "../api/gallery/galleryProgress";
import { getGallerySolution, } from "../api/gallery/gallerySolution";
import { getGallerySet } from "../api/gallery/getGallerySet";
import { toggleDoneTick } from "../gallery/doneTick";
import { GALLERY_POLL_MS, completedIndexOf, solutionSelectedIndex, tokensUpTo, } from "../gallery/grades";
import { loadGalleryMarks, saveGalleryMarks, toggleCollectedMark, toggleFirstOwnerMark, } from "../gallery/marks";
import { isUnauthorized, pollDelay } from "../gallery/poll";
const NO_MARKS = { collected: [], firstOwner: [] };
/**
 * One Gallery set's page on both platforms: the set, its solution (the stored
 * one, else the market solve), a solve polled until it lands, the completed
 * grade and the cards marked by hand.
 */
export const useGallerySet = ({ categorySlug, setSlug, userUuid, initialGrade, }) => {
    const [set, setSet] = useState();
    const [status, setStatus] = useState("pending");
    const [solution, setSolution] = useState(null);
    // The solution read has answered (a solution, none, or an error).
    const [solutionLoaded, setSolutionLoaded] = useState(false);
    const [buildStatus, setBuildStatus] = useState();
    // The set is queued or solving in the backend: poll until it lands.
    const [polling, setPolling] = useState(false);
    const [pickedGrade, setPickedGrade] = useState(initialGrade);
    const [completedGrade, setCompletedGrade] = useState(null);
    // The grade the backend last confirmed, and the latest save, so only the
    // newest failed save rolls back, and back to what is really stored.
    const savedGrade = useRef(null);
    const gradeSeq = useRef(0);
    const [marks, setMarks] = useState(NO_MARKS);
    // The marks as of the last tick, ahead of the render; toggles read this.
    const marksRef = useRef(marks);
    // After a grade is marked completed, with the tokens it added.
    const [justCompleted, setJustCompleted] = useState();
    useEffect(() => {
        let cancelled = false;
        setStatus("pending");
        getGallerySet(categorySlug, setSlug)
            .then((result) => {
            if (cancelled)
                return;
            setSet(result);
            setStatus("fulfilled");
        })
            .catch(() => {
            if (!cancelled)
                setStatus("rejected");
        });
        return () => {
            cancelled = true;
        };
    }, [categorySlug, setSlug]);
    const setId = set?.id;
    useEffect(() => {
        const loaded = setId ? loadGalleryMarks(setId) : NO_MARKS;
        marksRef.current = loaded;
        setMarks(loaded);
    }, [setId]);
    const active = set?.active;
    // The stored solution, else the market-only solve. Only a build solves.
    useEffect(() => {
        setSolution(null);
        setSolutionLoaded(false);
        setBuildStatus(undefined);
        setPolling(false);
        setCompletedGrade(null);
        savedGrade.current = null;
        if (!setId || !active)
            return;
        let cancelled = false;
        getGallerySolution(setId)
            .then((stored) => {
            if (!cancelled)
                setSolution(stored);
        })
            .catch(() => undefined)
            .finally(() => {
            if (!cancelled)
                setSolutionLoaded(true);
        });
        // Signed out, the market solve alone.
        if (!userUuid)
            return;
        getGalleryProgress()
            .then(({ sets }) => {
            if (cancelled)
                return;
            const progress = sets.find((p) => p.setId === setId);
            savedGrade.current = progress?.completedGrade ?? null;
            setCompletedGrade(savedGrade.current);
            // A run started elsewhere may be solving this set.
            const solveStatus = progress?.solveStatus;
            if (solveStatus !== "queued" && solveStatus !== "solving")
                return;
            setBuildStatus("pending");
            setPolling(true);
        })
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [setId, active, userUuid]);
    useEffect(() => {
        if (!polling || !setId)
            return;
        let cancelled = false;
        let timer;
        let failures = 0;
        const poll = () => {
            const delay = pollDelay(GALLERY_POLL_MS, failures);
            timer = setTimeout(() => {
                getGalleryProgress()
                    .then(async ({ sets }) => {
                    failures = 0;
                    const solveStatus = sets.find((p) => p.setId === setId)?.solveStatus;
                    if (cancelled)
                        return;
                    if (solveStatus === "queued" || solveStatus === "solving")
                        return poll();
                    if (solveStatus === "done") {
                        // Solved but unreadable: stop with an error, don't poll on.
                        const stored = await getGallerySolution(setId).catch(() => undefined);
                        if (cancelled)
                            return;
                        if (stored === undefined) {
                            setBuildStatus("rejected");
                            setPolling(false);
                            return;
                        }
                        setSolution(stored);
                    }
                    setBuildStatus(solveStatus === "done" ? "fulfilled" : "rejected");
                    setPolling(false);
                })
                    .catch((error) => {
                    if (cancelled)
                        return;
                    if (isUnauthorized(error)) {
                        setBuildStatus("rejected");
                        setPolling(false);
                        return;
                    }
                    failures += 1;
                    poll();
                });
            }, delay);
        };
        poll();
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [polling, setId]);
    /** Queues this set's solve; the poll picks it up. */
    const buildSolution = async () => {
        if (!setId)
            return;
        setBuildStatus("pending");
        try {
            await startGallerySolves({ setId });
            setPolling(true);
        }
        catch {
            setBuildStatus("rejected");
        }
    };
    const changeCompletedGrade = (grade) => {
        if (!setId)
            return Promise.resolve(false);
        const seq = ++gradeSeq.current;
        setCompletedGrade(grade);
        // Resolves to whether it saved, for a caller that wants to say so.
        return setGalleryCompletedGrade(setId, grade).then(() => {
            const previous = savedGrade.current;
            savedGrade.current = grade;
            if (!grade || !set)
                return true;
            const indexOf = (name) => completedIndexOf(set.grades, name);
            // Unmarking saves the grade below: only a higher grade celebrates.
            if (indexOf(grade) <= indexOf(previous))
                return true;
            setJustCompleted({
                grade,
                tokens: tokensUpTo(set.grades, indexOf(grade)) -
                    tokensUpTo(set.grades, indexOf(previous)),
            });
            return true;
        }, () => {
            if (seq === gradeSeq.current)
                setCompletedGrade(savedGrade.current);
            return false;
        });
    };
    // Stable, so memoized solution cards don't all re-render on each tick. The ref moves with each tick, so a
    // second tap before the re-render already sees the first.
    const updateMarks = useCallback((toggle, id) => {
        if (!setId)
            return;
        const next = toggle(marksRef.current, id);
        marksRef.current = next;
        saveGalleryMarks(setId, next);
        setMarks(next);
    }, [setId]);
    // A tick adds a collected player (Done tick), un-ticking deletes it again.
    const toggleCollected = useCallback((id) => toggleDoneTick(id, (i) => marksRef.current.collected.includes(i), (i, ticked) => updateMarks((prev) => prev.collected.includes(i) === ticked
        ? prev
        : toggleCollectedMark(prev, i), i), !!userUuid), [updateMarks, userUuid]);
    // Marking First Owner adds the collected player first, as the backend only flips existing rows. A saved change
    // outdates the solution; a failed one puts the mark back unless another toggle moved it since.
    const toggleFirstOwner = useCallback((id) => {
        const isMarked = () => marksRef.current.firstOwner.includes(id);
        const marking = !isMarked();
        updateMarks(toggleFirstOwnerMark, id);
        if (!userUuid)
            return;
        const save = marking
            ? addGalleryDoneTick(id).then(() => setCollectedFirstOwner([id], true))
            : setCollectedFirstOwner([id], false);
        save.then(() => setSolution((s) => s && { ...s, upToDate: false }), () => {
            if (isMarked() === marking)
                updateMarks(toggleFirstOwnerMark, id);
        });
    }, [updateMarks, userUuid]);
    // Collected / First Owner per the backend or marked here, by resource id.
    const collectedIds = useMemo(() => new Set([
        ...marks.collected,
        ...(solution?.grades.flatMap((g) => g.items.filter((i) => i.collected).map((i) => i.resourceId)) ?? []),
    ]), [marks.collected, solution]);
    const firstOwnerIds = useMemo(() => new Set([
        ...marks.firstOwner,
        ...(solution?.grades.flatMap((g) => g.items.filter((i) => i.firstOwner).map((i) => i.resourceId)) ?? []),
    ]), [marks.firstOwner, solution]);
    const completedIndex = set
        ? completedIndexOf(set.grades, completedGrade)
        : -1;
    const grades = solution?.grades ?? [];
    const selectedIndex = solutionSelectedIndex(grades, pickedGrade, completedIndex);
    const grade = selectedIndex !== undefined ? grades[selectedIndex] : undefined;
    const refreshing = buildStatus === "pending";
    const collectedCount = grade?.items.filter((i) => collectedIds.has(i.resourceId)).length ?? 0;
    return {
        set,
        status,
        solution,
        /** The first solution read answered; until then the page is loading. */
        solutionLoaded,
        buildStatus,
        refreshing,
        /** The user's own solution, solved after the latest content release and newest collected player. */
        solutionBuilt: solution?.source === "user" && solution.upToDate && !refreshing,
        buildSolution,
        completedGrade,
        completedIndex,
        changeCompletedGrade,
        justCompleted,
        dismissJustCompleted: () => setJustCompleted(undefined),
        grades,
        selectedIndex,
        grade,
        pickGrade: setPickedGrade,
        collectedCount,
        marks,
        collectedIds,
        firstOwnerIds,
        toggleCollected,
        toggleFirstOwner,
    };
};
//# sourceMappingURL=useGallerySet.js.map